import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Play, Pause, CheckCircle, Camera, MapPin,
  Star, Send, Clock, AlertCircle, Upload, Image, Loader2, 
  Sparkles, Leaf, Waves, Wind, Wrench, ThumbsUp, MessageSquare
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  cat_diarista: Sparkles,
  cat_jardinagem: Leaf,
  cat_piscina: Waves,
  cat_arcondicionado: Wind,
  cat_reforma: Wrench
};

const RATING_TYPES = [
  { id: "punctuality", label: "Pontualidade", icon: Clock },
  { id: "quality", label: "Qualidade", icon: Star },
  { id: "communication", label: "Comunicação", icon: MessageSquare },
  { id: "professionalism", label: "Profissionalismo", icon: ThumbsUp }
];

export default function ExecutionPage() {
  const { contractId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [updates, setUpdates] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [liveRatings, setLiveRatings] = useState([]);
  const [escrow, setEscrow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Update form state
  const [updateDescription, setUpdateDescription] = useState("");
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [photos, setPhotos] = useState([]);
  
  // Rating state
  const [selectedRatingType, setSelectedRatingType] = useState(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingComment, setRatingComment] = useState("");

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
    // Poll for updates every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [contractId, token]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };

      const [contractRes, updatesRes, checkpointsRes, ratingsRes] = await Promise.all([
        fetch(`${API_URL}/api/contracts/${contractId}`, { headers }),
        fetch(`${API_URL}/api/execution/updates/${contractId}`, { headers }),
        fetch(`${API_URL}/api/execution/checkpoints/${contractId}`, { headers }),
        fetch(`${API_URL}/api/ratings/live/${contractId}`, { headers })
      ]);

      if (contractRes.ok) {
        const contractData = await contractRes.json();
        setContract(contractData);
        setProgressPercentage(contractData.progress_percentage || 0);
        
        // Fetch escrow
        const escrowRes = await fetch(`${API_URL}/api/escrows`, { headers });
        if (escrowRes.ok) {
          const escrows = await escrowRes.json();
          const contractEscrow = escrows.find(e => e.contract_id === contractId);
          setEscrow(contractEscrow);
        }
      }
      if (updatesRes.ok) setUpdates(await updatesRes.json());
      if (checkpointsRes.ok) setCheckpoints(await checkpointsRes.json());
      if (ratingsRes.ok) setLiveRatings(await ratingsRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const response = await fetch(`${API_URL}/api/upload/image`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (response.ok) {
          const data = await response.json();
          setPhotos(prev => [...prev, data.url]);
        }
      } catch (error) {
        toast.error("Erro ao fazer upload da foto");
      }
    }
  };

  const submitUpdate = async (updateType) => {
    if (!updateDescription.trim() && updateType !== "start") {
      toast.error("Descreva a atualização");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/execution/update`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: contractId,
          update_type: updateType,
          description: updateDescription || `Serviço ${updateType === "start" ? "iniciado" : updateType === "complete" ? "concluído" : "atualizado"}`,
          progress_percentage: progressPercentage,
          photos: photos
        })
      });

      if (response.ok) {
        toast.success(
          updateType === "start" ? "Serviço iniciado!" :
          updateType === "complete" ? "Serviço finalizado!" :
          "Atualização enviada!"
        );
        setUpdateDescription("");
        setPhotos([]);
        fetchData();
      } else {
        throw new Error("Erro ao enviar atualização");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const submitRating = async () => {
    if (!selectedRatingType || ratingValue === 0) {
      toast.error("Selecione um tipo e uma nota");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/ratings/live`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: contractId,
          rating_type: selectedRatingType,
          rating: ratingValue,
          comment: ratingComment || null
        })
      });

      if (response.ok) {
        toast.success("Avaliação enviada!");
        setSelectedRatingType(null);
        setRatingValue(0);
        setRatingComment("");
        fetchData();
      } else {
        throw new Error("Erro ao enviar avaliação");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const approveCheckpoint = async (checkpointId) => {
    try {
      const response = await fetch(`${API_URL}/api/execution/checkpoint/${checkpointId}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Checkpoint aprovado!");
        fetchData();
      }
    } catch (error) {
      toast.error("Erro ao aprovar checkpoint");
    }
  };

  const approveService = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/approve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          notes: "Serviço aprovado",
          final_comment: "Excelente trabalho!"
        })
      });

      if (response.ok) {
        toast.success("Serviço aprovado! Pagamento liberado.");
        navigate("/contracts");
      }
    } catch (error) {
      toast.error("Erro ao aprovar serviço");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <Card className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Contrato não encontrado</p>
          <Button className="mt-4" onClick={() => navigate("/contracts")}>
            Voltar
          </Button>
        </Card>
      </div>
    );
  }

  const Icon = CATEGORY_ICONS[contract.category_id] || Sparkles;
  const isProvider = user?.role === "provider";
  const isClient = user?.role === "client";
  const canStart = isProvider && contract.status === "active";
  const canUpdate = isProvider && ["in_execution", "active"].includes(contract.status);
  const canRate = isClient && ["in_execution", "pending_approval"].includes(contract.status);
  const canApprove = isClient && contract.status === "pending_approval";

  return (
    <div className="min-h-screen bg-stone-50" data-testid="execution-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/contracts")} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">Execução do Serviço</p>
                  <p className="text-xs text-slate-500">#{contract.contract_id.slice(-8)}</p>
                </div>
              </div>
            </div>
            <Badge className={
              contract.status === "in_execution" ? "bg-blue-100 text-blue-700" :
              contract.status === "pending_approval" ? "bg-amber-100 text-amber-700" :
              contract.status === "approved" || contract.status === "completed" ? "bg-emerald-100 text-emerald-700" :
              "bg-slate-100 text-slate-700"
            }>
              {contract.status === "active" ? "Aguardando Início" :
               contract.status === "in_execution" ? "Em Execução" :
               contract.status === "pending_approval" ? "Aguardando Aprovação" :
               contract.status === "approved" ? "Aprovado" :
               contract.status === "completed" ? "Concluído" :
               contract.status}
            </Badge>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Progress Card */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Progresso do Serviço</h3>
              <span className="text-2xl font-bold text-emerald-600">{progressPercentage}%</span>
            </div>
            <Progress value={progressPercentage} className="h-3" />
            <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
              <span>Início</span>
              <span>Conclusão</span>
            </div>
          </CardContent>
        </Card>

        {/* Live Rating Summary */}
        {contract.live_rating_avg > 0 && (
          <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
                  <Star className="w-7 h-7 text-amber-500 fill-amber-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-amber-700">Avaliação em Tempo Real</p>
                  <p className="text-3xl font-bold text-amber-600">{contract.live_rating_avg.toFixed(1)}</p>
                  <p className="text-xs text-amber-600">{contract.live_ratings_count} avaliações</p>
                </div>
                {contract.live_ratings_by_type && (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {Object.entries(contract.live_ratings_by_type).map(([type, value]) => (
                      <div key={type} className="flex items-center gap-1">
                        <span className="text-amber-700 capitalize">{type}:</span>
                        <span className="font-bold">{value.toFixed(1)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Provider Controls */}
        {isProvider && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Atualizar Execução
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Start/Complete buttons */}
              {canStart && (
                <Button 
                  className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                  onClick={() => submitUpdate("start")}
                  disabled={submitting}
                  data-testid="start-service-btn"
                >
                  <Play className="w-5 h-5 mr-2" />
                  Iniciar Serviço
                </Button>
              )}

              {canUpdate && (
                <>
                  {/* Progress slider */}
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      Progresso: {progressPercentage}%
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={progressPercentage}
                      onChange={(e) => setProgressPercentage(parseInt(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>

                  {/* Description */}
                  <Textarea
                    placeholder="Descreva o andamento do serviço..."
                    value={updateDescription}
                    onChange={(e) => setUpdateDescription(e.target.value)}
                    rows={3}
                    data-testid="update-description"
                  />

                  {/* Photo upload */}
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handlePhotoUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      Adicionar Fotos
                    </Button>
                    {photos.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {photos.map((url, idx) => (
                          <div key={idx} className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100">
                            <img src={`${API_URL}${url}`} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1"
                      onClick={() => submitUpdate("progress")}
                      disabled={submitting}
                      data-testid="send-update-btn"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Enviar Atualização
                    </Button>
                    {progressPercentage === 100 && (
                      <Button 
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => submitUpdate("complete")}
                        disabled={submitting}
                        data-testid="complete-service-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Finalizar Serviço
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Client Rating Controls */}
        {canRate && (
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-800">
                <Star className="w-5 h-5" />
                Avaliar em Tempo Real
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Avalie o serviço enquanto ele acontece. Suas avaliações ajudam a garantir qualidade!
              </p>

              {/* Rating type selection */}
              <div className="grid grid-cols-2 gap-2">
                {RATING_TYPES.map((type) => {
                  const TypeIcon = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => setSelectedRatingType(type.id)}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        selectedRatingType === type.id 
                          ? "border-emerald-500 bg-emerald-100" 
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`rating-type-${type.id}`}
                    >
                      <TypeIcon className={`w-4 h-4 ${selectedRatingType === type.id ? "text-emerald-600" : "text-slate-500"}`} />
                      <span className={`text-sm font-medium ${selectedRatingType === type.id ? "text-emerald-700" : "text-slate-700"}`}>
                        {type.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Star rating */}
              {selectedRatingType && (
                <div className="space-y-3">
                  <div className="flex items-center justify-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRatingValue(star)}
                        className="p-1 transition-transform hover:scale-110"
                        data-testid={`star-${star}`}
                      >
                        <Star 
                          className={`w-8 h-8 ${
                            star <= ratingValue 
                              ? "text-amber-400 fill-amber-400" 
                              : "text-slate-300"
                          }`} 
                        />
                      </button>
                    ))}
                  </div>

                  <Textarea
                    placeholder="Comentário opcional..."
                    value={ratingComment}
                    onChange={(e) => setRatingComment(e.target.value)}
                    rows={2}
                  />

                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={submitRating}
                    disabled={submitting || ratingValue === 0}
                    data-testid="submit-rating-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Star className="w-4 h-4 mr-2" />}
                    Enviar Avaliação
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Approve Service Button */}
        {canApprove && (
          <Card className="border-emerald-300 bg-gradient-to-r from-emerald-50 to-emerald-100">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-emerald-800 text-lg">Serviço Concluído!</h3>
                  <p className="text-emerald-600 text-sm">Aprove para liberar o pagamento ao prestador</p>
                </div>
                <Button 
                  className="bg-emerald-600 hover:bg-emerald-700 h-12 px-8"
                  onClick={approveService}
                  data-testid="approve-service-btn"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Aprovar e Liberar Pagamento
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Updates Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Histórico de Atualizações
            </CardTitle>
          </CardHeader>
          <CardContent>
            {updates.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Nenhuma atualização ainda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {updates.map((update, index) => (
                  <div 
                    key={update.update_id} 
                    className={`relative pl-6 pb-4 ${index !== updates.length - 1 ? "border-l-2 border-slate-200" : ""}`}
                  >
                    <div className={`absolute left-0 top-0 w-3 h-3 rounded-full -translate-x-[7px] ${
                      update.update_type === "start" ? "bg-emerald-500" :
                      update.update_type === "complete" ? "bg-blue-500" :
                      "bg-slate-400"
                    }`}></div>
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-xs">
                          {update.update_type === "start" ? "Início" :
                           update.update_type === "complete" ? "Conclusão" :
                           update.update_type === "checkpoint" ? "Checkpoint" :
                           "Progresso"}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(update.created_at).toLocaleString("pt-BR")}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700">{update.description}</p>
                      {update.progress_percentage > 0 && (
                        <div className="mt-2">
                          <Progress value={update.progress_percentage} className="h-2" />
                          <span className="text-xs text-slate-500">{update.progress_percentage}%</span>
                        </div>
                      )}
                      {update.photos?.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {update.photos.map((photo, idx) => (
                            <div key={idx} className="w-20 h-20 rounded-lg overflow-hidden">
                              <img src={`${API_URL}${photo}`} alt="" className="w-full h-full object-cover" />
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Checkpoints */}
        {checkpoints.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Etapas do Serviço
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {checkpoints.map((checkpoint) => (
                  <div 
                    key={checkpoint.checkpoint_id}
                    className={`p-4 rounded-lg border-2 ${
                      checkpoint.status === "approved" ? "border-emerald-200 bg-emerald-50" :
                      checkpoint.status === "pending" ? "border-amber-200 bg-amber-50" :
                      "border-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-slate-900">{checkpoint.title}</h4>
                        <p className="text-sm text-slate-500">{checkpoint.description}</p>
                      </div>
                      {checkpoint.status === "pending" && isClient && (
                        <Button 
                          size="sm"
                          onClick={() => approveCheckpoint(checkpoint.checkpoint_id)}
                          data-testid={`approve-checkpoint-${checkpoint.checkpoint_id}`}
                        >
                          Aprovar
                        </Button>
                      )}
                      {checkpoint.status === "approved" && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Aprovado
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Live Ratings List */}
        {liveRatings.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5" />
                Avaliações em Tempo Real
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {liveRatings.slice(0, 10).map((rating) => (
                  <div key={rating.rating_id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          className={`w-4 h-4 ${star <= rating.rating ? "text-amber-400 fill-amber-400" : "text-slate-300"}`}
                        />
                      ))}
                    </div>
                    <Badge variant="outline" className="capitalize text-xs">
                      {rating.rating_type}
                    </Badge>
                    {rating.comment && (
                      <span className="text-sm text-slate-600 flex-1">{rating.comment}</span>
                    )}
                    <span className="text-xs text-slate-400">
                      {new Date(rating.created_at).toLocaleTimeString("pt-BR")}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Escrow Info */}
        {escrow && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Informações do Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500">Total</p>
                  <p className="text-lg font-bold text-slate-900">
                    R$ {escrow.total_amount?.toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-lg">
                  <p className="text-xs text-emerald-600">Depositado</p>
                  <p className="text-lg font-bold text-emerald-700">
                    R$ {escrow.deposited_amount?.toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-600">Liberado</p>
                  <p className="text-lg font-bold text-blue-700">
                    R$ {escrow.released_amount?.toLocaleString("pt-BR")}
                  </p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-600">Retenção</p>
                  <p className="text-lg font-bold text-amber-700">
                    R$ {escrow.retained_amount?.toLocaleString("pt-BR")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
